// MobileCalendarShell.tsx — Orchestrator der Mobile-Kalenderansicht
// (`/termine`, SP6). Komponiert: MobileTopBar → (DaySelector nur in Tagesansicht)
// → aktive View (MobileDayGrid | DayAgendaView | TimeGridView(Woche) |
// MobileMonthView) → FABButton → zwei Bottom-Sheets (Filter + Kalender).
//
// Quellen: useCalendarState() (view/date/filter/selectedCalendars/events aus
// SP5), onSelectSlot-Pfad (SP3, Tap-Slot-Create) und openTerminFormWindow
// (SP2, FAB + Event-Klick + Slot-Create) mit contactId number|null (A1).
//
// Geteilte Ansicht ist mobil IMMER MERGED (kein Toggle): >1 gewählter Kalender
// ⇒ EINE Spalte/Liste, source.color als linker Rand. Der Context-Wert `shared`
// wird mobil ignoriert (Desktop-only).

import React, { useCallback, useMemo, useState } from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import { useMailContext } from "../../context/MailContext";
import {
    anzahlGewaehlt,
    filterEintraege,
    gruppiereQuellen,
    istGewaehlt,
    nurGewaehlte,
    type KalenderEintrag,
} from "./kalenderPickerGruppen";
import { useAuth } from "../../context/AuthContext";
import { resolveDefaultCalendarMailbox } from "../../utils/mailboxAccess";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import {
    openTerminFormWindow,
    type TerminPrefill,
} from "../windowManager/openTerminFormWindow";
import { useCalendarState } from "./CalendarStateContext";
import type { CalendarGridEvent } from "./types";
import MobileTopBar from "./MobileTopBar";
import DaySelector from "./DaySelector";
import MobileDayGrid from "./MobileDayGrid";
import DayAgendaView from "./DayAgendaView";
import MobileMonthView from "./MobileMonthView";
import TimeGridView from "./TimeGridView";
import FABButton from "./FABButton";
import BottomSheet from "./BottomSheet";
import TerminPeekPopover from "./TerminPeekPopover";
import { HinweisToast, useHinweisToast } from "./HinweisToast";
import { inklusivesAllDayEnde } from "./allDay";
import { auswahlAusGridEvent } from "./calendarAuswahl";
import CalendarModalKopf from "./CalendarModalKopf";
import type { CalendarShellProps } from "./CalendarView";

/** CalendarGridEvent → Minimal-Prefill fürs Termin-Fenster (wie CalendarView). */
const toPrefill = (e: CalendarGridEvent): TerminPrefill => ({
    id: e.id,
    title: e.title,
    start: e.start,
    end: e.end,
    allDay: e.allDay,
    location: e.location,
    mailbox: e.mailbox,
});

/**
 * K6 — der Telefon-Pfad ist eine ZWEITE, eigenständige Komposition mit eigenem
 * onSelectEvent, eigenen openTerminFormWindow-Aufrufen und eigenem FAB. Er
 * bekommt deshalb EXAKT dieselben Shell-Props wie die Desktop-Shell; ohne sie
 * (propfreier Mount auf der Route /termine) bleibt alles beim Alten.
 */
export const MobileCalendarShell: React.FC<Partial<CalendarShellProps>> = (shell = {}) => {
    const auswahl = shell.modus === "auswahl";
    const { openWindow, closeWindow } = useWindowManager();
    const { currentMailbox, mailboxes, calendarMailboxes, ownMailbox } = useMailContext();
    const { user } = useAuth();
    // Eigene Login-Adresse als Fallback für die Eigen-Postfach-Erkennung, falls
    // das Backend-Flag `isOwn` an einem Postfach fehlt (Alt-API).
    const ownAddress = (user?.email as string | undefined) ?? null;
    // SP2: sichtbares Feedback statt stillem No-Op (kein WRITE-Kalender vorhanden).
    const { hinweis, zeigeHinweis } = useHinweisToast();
    const {
        view,
        setView,
        currentDate,
        setCurrentDate,
        filter,
        toggleFilter,
        selectedCalendars,
        toggleCalendar,
        calendarColor,
        events,
        filteredEvents,
    } = useCalendarState();

    const [dayMode, setDayMode] = useState<"grid" | "agenda">("grid");
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [calSheetOpen, setCalSheetOpen] = useState(false);
    // T-69: zweite Ebene im Kalender-Sheet — alle Räume bzw. alle
    // freigegebenen Kollegen mit Suche.
    const [calUnterebene, setCalUnterebene] = useState<"raeume" | "kollegen" | null>(null);
    const [calSuche, setCalSuche] = useState("");

    // WRITE-Gating je Postfach (echtes calendarAccess, wie CalendarView, B1).
    const canWriteMailbox = useCallback(
        (mailbox: string | null | undefined) => {
            if (!mailbox) return false;
            const mb = mailboxes.find(
                (m) => m.mailboxAddress.toLowerCase() === mailbox.toLowerCase(),
            );
            return mb?.calendarAccess === "WRITE";
        },
        [mailboxes],
    );

    const canEditEvent = useCallback(
        (e: CalendarGridEvent) =>
            canWriteMailbox(e.mailbox) && !(e.type === "occurrence" || e.type === "exception"),
        [canWriteMailbox],
    );

    // Volles Termin-Fenster (Peek-Aktion „Öffnen"; Fenster = Vollbild-Panel).
    const onOpenEdit = useCallback(
        (e: CalendarGridEvent) => {
            openTerminFormWindow(openWindow, {
                editEventId: e.id,
                mailbox: e.mailbox,
                prefillEvent: toPrefill(e),
                canEdit: canEditEvent(e),
            });
        },
        [openWindow, canEditEvent],
    );

    /** „Fertig" im Auswahlmodus — das Fenster schließt sich selbst. */
    const onFertig = useCallback(() => {
        if (shell.windowId) closeWindow(shell.windowId);
    }, [closeWindow, shell.windowId]);

    // Event-Tap → Peek als BottomSheet (SP4, A16 mobil; Entscheidung 69):
    // erst die Kurzübersicht, „Öffnen" führt ins Vollbild-Fenster.
    const [peekEvent, setPeekEvent] = useState<CalendarGridEvent | null>(null);
    // K6: im Auswahlmodus verknüpft der Tap sofort — kein BottomSheet-Peek.
    const onSelectEvent = useCallback(
        (e: CalendarGridEvent) => {
            if (auswahl && shell.onPick) {
                void shell.onPick(auswahlAusGridEvent(e));
                return;
            }
            setPeekEvent(e);
        },
        [auswahl, shell.onPick],
    );

    // Tap-auf-Slot → Neuanlage mit Zeit-Prefill (SP3/SP2-Pfad).
    //
    // Bis zum 22.09.2026 gewann `currentMailbox`, sofern es Schreibrecht hatte
    // — das im Mail-Modul gewählte Postfach, meist cbb@. PO: „bei neuen Termin
    // immer die eigene Adresse verwenden." Das eigene Postfach gewinnt jetzt
    // immer. Die Prüfung bleibt für den Fall, dass GAR kein Kalender
    // Schreibrecht hat: dann ein Toast statt eines stillen `return` (SP2,
    // Review 2026-07-07 — das war die Ursache für „Selektion öffnet kein Popup").
    const onSelectSlot = useCallback(
        (slot: { start: Date; end: Date; allDay: boolean }) => {
            const zielMailbox =
                resolveDefaultCalendarMailbox(mailboxes, ownAddress)?.mailboxAddress ?? null;
            if (!zielMailbox) {
                zeigeHinweis("Kein Kalender mit Schreibrecht — Termin kann nicht angelegt werden.");
                return;
            }
            // Ganztags-Selektion: exklusives Ende → inklusiver letzter Tag
            // (TerminFormWindow addiert beim Speichern selbst +1 Tag).
            const endeFuersFormular = slot.allDay
                ? inklusivesAllDayEnde(slot.start, slot.end)
                : slot.end;
            openTerminFormWindow(openWindow, {
                initialStart: slot.start,
                initialEnd: endeFuersFormular,
                allDay: slot.allDay,
                mailbox: zielMailbox,
                ...(shell.neuTerminPrefill ?? {}),
            });
        },
        [openWindow, mailboxes, ownAddress, zeigeHinweis, shell.neuTerminPrefill],
    );

    // FAB → Neuanlage ohne Zeit-Prefill. Ohne `mailbox`: Das eigene Postfach
    // ist die Vorauswahl des Dialogs. K6: im Auswahlmodus trägt auch der
    // FAB-Termin die Vorbelegung des Aufrufers — sonst bliebe ein auf dem
    // Telefon angelegter Termin unverknüpft.
    const onNewTermin = useCallback(() => {
        openTerminFormWindow(openWindow, { ...(shell.neuTerminPrefill ?? {}) });
    }, [openWindow, shell.neuTerminPrefill]);

    // Mobil immer MERGED: ein einziger Event-Strom (gefiltert), Farbe via Rand.
    const dayPickHandler = useCallback(
        (d: Date) => {
            setCurrentDate(d);
            setView("day");
        },
        [setCurrentDate, setView],
    );

    // Filter-Badge: Anzahl ausgeblendeter Kategorien.
    const hiddenFilterCount = useMemo(
        () =>
            (filter.kundentermin ? 0 : 1) +
            (filter.austausch ? 0 : 1) +
            // K9: „Nur Termine mit Vorgang" zählt umgekehrt — es schränkt ein.
            (filter.nurMitVorgang ? 1 : 0),
        [filter],
    );

    // Verfügbare Kalender fürs Kalender-Sheet — dieselben Gruppen wie der
    // Desktop-Picker (T-69): Meine Kalender, Kollegen, gewählte Räume.
    const kalenderGruppen = useMemo(
        () =>
            gruppiereQuellen(
                calendarMailboxes && calendarMailboxes.length > 0 ? calendarMailboxes : mailboxes,
                ownMailbox ?? currentMailbox,
            ),
        [calendarMailboxes, mailboxes, ownMailbox, currentMailbox],
    );

    const selectedCalendarCount = anzahlGewaehlt(kalenderGruppen, selectedCalendars);

    const renderKalenderZeile = (it: KalenderEintrag) => (
        <label key={it.address} className="ct-sheet-check">
            <input
                type="checkbox"
                checked={istGewaehlt(it.address, selectedCalendars)}
                onChange={() => toggleCalendar(it.address)}
            />
            <span
                aria-hidden
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: calendarColor(it.address),
                    flex: "0 0 auto",
                }}
            />
            <span style={calNameStyle} title={it.address}>
                {it.displayName}
                {it.isOwn && <span style={ownTagStyle}>eigen</span>}
                {it.stufeHinweis && <span style={hintStyle}> · {it.stufeHinweis}</span>}
            </span>
        </label>
    );

    return (
        <div style={shellStyle}>
            {auswahl && !shell.imOverlay && (
                <CalendarModalKopf
                    titel={shell.titel ?? "Termin verknüpfen"}
                    hinweis={shell.hinweis}
                    onFertig={shell.windowId ? onFertig : undefined}
                />
            )}
            <MobileTopBar
                dayMode={dayMode}
                onDayModeChange={setDayMode}
                onOpenFilter={() => setFilterSheetOpen(true)}
                onOpenCalendars={() => {
                    setCalUnterebene(null);
                    setCalSheetOpen(true);
                }}
                hiddenFilterCount={hiddenFilterCount}
                selectedCalendarCount={selectedCalendarCount}
            />

            {/* Tages-Selektor nur in der Tagesansicht. */}
            {view === "day" && (
                <DaySelector value={currentDate} onChange={setCurrentDate} events={events} />
            )}

            <div style={contentStyle}>
                {view === "day" && dayMode === "grid" && (
                    <MobileDayGrid
                        events={filteredEvents}
                        onSelectEvent={onSelectEvent}
                        onSelectSlot={onSelectSlot}
                    />
                )}
                {view === "day" && dayMode === "agenda" && (
                    <DayAgendaView
                        date={currentDate}
                        events={filteredEvents}
                        onSelectEvent={onSelectEvent}
                    />
                )}
                {view === "week" && (
                    // Woche bleibt das RBC-Grid (DaySelector ausgeblendet). Touch-
                    // Drag bewusst aus → nur onSelectEvent/onSelectSlot.
                    <TimeGridView
                        events={filteredEvents}
                        onSelectEvent={onSelectEvent}
                        onSelectSlot={onSelectSlot}
                    />
                )}
                {view === "month" && (
                    <MobileMonthView
                        value={currentDate}
                        onPickDay={dayPickHandler}
                        events={events}
                    />
                )}
            </div>

            <FABButton onClick={onNewTermin} />

            {/* Filter-Sheet (Kundentermin/Austausch). */}
            <BottomSheet
                open={filterSheetOpen}
                title="Filter"
                onClose={() => setFilterSheetOpen(false)}
                footer={
                    <>
                        <button
                            type="button"
                            style={sheetGhostBtn}
                            onClick={() => {
                                if (!filter.kundentermin) toggleFilter("kundentermin");
                                if (!filter.austausch) toggleFilter("austausch");
                                if (!filter.sonstige) toggleFilter("sonstige");
                                if (filter.nurMitVorgang) toggleFilter("nurMitVorgang");
                            }}
                        >
                            Zurücksetzen
                        </button>
                        <button
                            type="button"
                            style={sheetPrimaryBtn}
                            onClick={() => setFilterSheetOpen(false)}
                        >
                            Fertig
                        </button>
                    </>
                }
            >
                <div className="ct-sheet-fld">
                    <span className="ct-sheet-lbl">Anzeigen</span>
                    <div className="ct-sheet-checks">
                        <label className="ct-sheet-check">
                            <input
                                type="checkbox"
                                checked={filter.kundentermin}
                                onChange={() => toggleFilter("kundentermin")}
                            />
                            <span>Kundentermine</span>
                        </label>
                        <label className="ct-sheet-check">
                            <input
                                type="checkbox"
                                checked={filter.austausch}
                                onChange={() => toggleFilter("austausch")}
                            />
                            <span>Austausch / Intern</span>
                        </label>
                        {/* PO 21.09.2026: dritte Art, hier genauso schaltbar wie
                            auf dem Desktop — sonst ließe sie sich mobil nie
                            wieder einblenden, wenn sie am Desktop aus ist. */}
                        <label className="ct-sheet-check">
                            <input
                                type="checkbox"
                                checked={filter.sonstige}
                                onChange={() => toggleFilter("sonstige")}
                            />
                            <span>Sonstige</span>
                        </label>
                        {/* K9: dieselbe Einschränkung wie auf dem Desktop. */}
                        <label className="ct-sheet-check">
                            <input
                                type="checkbox"
                                checked={filter.nurMitVorgang}
                                onChange={() => toggleFilter("nurMitVorgang")}
                            />
                            <span>Nur Termine mit Vorgang</span>
                        </label>
                        {/* PO 28.09.2026: Ebene „Termine meiner Kontakte". */}
                        <label className="ct-sheet-check">
                            <input
                                type="checkbox"
                                checked={filter.kontaktTermine}
                                onChange={() => toggleFilter("kontaktTermine")}
                            />
                            <span>Termine meiner Kontakte</span>
                        </label>
                    </div>
                    <span style={hintStyle}>
                        Pflicht-Blöcke &amp; Abwesenheiten bleiben immer sichtbar.
                    </span>
                </div>
            </BottomSheet>

            {/* Kalender-Auswahl-Sheet (>1 ⇒ automatisch merged, kein Toggle). */}
            <BottomSheet
                open={calSheetOpen}
                title="Kalender"
                onClose={() => setCalSheetOpen(false)}
                footer={
                    <button
                        type="button"
                        style={sheetPrimaryBtn}
                        onClick={() => setCalSheetOpen(false)}
                    >
                        Fertig
                    </button>
                }
            >
                <div className="ct-sheet-fld">
                    {calUnterebene ? (
                        <>
                            <button
                                type="button"
                                style={sheetLinkBtn}
                                onClick={() => setCalUnterebene(null)}
                            >
                                ‹ Zurück zur Kalenderauswahl
                            </button>
                            <input
                                type="search"
                                value={calSuche}
                                onChange={(e) => setCalSuche(e.target.value)}
                                placeholder={
                                    calUnterebene === "kollegen"
                                        ? "Kollegin/Kollegen suchen …"
                                        : "Raum suchen …"
                                }
                                aria-label={calUnterebene === "kollegen" ? "Kollegen suchen" : "Raum suchen"}
                                style={sheetSearchStyle}
                            />
                            <div className="ct-sheet-checks" style={{ maxHeight: "46vh" }}>
                                {filterEintraege(
                                    calUnterebene === "kollegen"
                                        ? kalenderGruppen.kollegen
                                        : kalenderGruppen.raeume,
                                    calSuche,
                                ).map(renderKalenderZeile)}
                            </div>
                        </>
                    ) : kalenderGruppen.eigene.length +
                          kalenderGruppen.kollegen.length +
                          kalenderGruppen.raeume.length ===
                      0 ? (
                        <span style={hintStyle}>Keine lesbaren Kalender.</span>
                    ) : (
                        <div className="ct-sheet-checks" style={{ maxHeight: "52vh" }}>
                            {kalenderGruppen.eigene.length > 0 && (
                                <span style={sheetHeadingStyle}>Meine Kalender</span>
                            )}
                            {kalenderGruppen.eigene.map(renderKalenderZeile)}
                            {kalenderGruppen.kollegen.length > 0 && (
                                <>
                                    <span style={sheetHeadingStyle}>Kollegen</span>
                                    {nurGewaehlte(kalenderGruppen.kollegen, selectedCalendars).map(
                                        renderKalenderZeile,
                                    )}
                                    <button
                                        type="button"
                                        style={sheetLinkBtn}
                                        onClick={() => {
                                            setCalSuche("");
                                            setCalUnterebene("kollegen");
                                        }}
                                    >
                                        + Weitere Kollegen …
                                    </button>
                                </>
                            )}
                            {kalenderGruppen.raeume.length > 0 && (
                                <>
                                    <span style={sheetHeadingStyle}>Raumkalender</span>
                                    {nurGewaehlte(kalenderGruppen.raeume, selectedCalendars).map(
                                        renderKalenderZeile,
                                    )}
                                    <button
                                        type="button"
                                        style={sheetLinkBtn}
                                        onClick={() => {
                                            setCalSuche("");
                                            setCalUnterebene("raeume");
                                        }}
                                    >
                                        + Raum hinzufügen …
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </BottomSheet>

            {/* SP4: Einzeltap-Peek als BottomSheet (A16 mobil). */}
            <TerminPeekPopover
                peek={peekEvent ? { event: peekEvent, anchor: null } : null}
                onClose={() => setPeekEvent(null)}
                canEditEvent={canEditEvent}
                onOpenEdit={onOpenEdit}
            />
            {/* SP2: sichtbarer Hinweis, wenn eine Anlage nicht möglich ist. */}
            <HinweisToast text={hinweis} />
        </div>
    );
};

const shellStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    background: REFINED.white,
    position: "relative",
};

const contentStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    padding: 8,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
};

const sheetHeadingStyle: React.CSSProperties = {
    display: "block",
    padding: "8px 0 2px",
    color: REFINED.ink3,
    font: `700 11px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.4,
};

const sheetLinkBtn: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    minHeight: 44,
    padding: 0,
    border: "none",
    background: "transparent",
    color: REFINED.aub,
    font: `600 15px ${REFINED.fontUI}`,
    cursor: "pointer",
    textAlign: "left",
};

const sheetSearchStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 44,
    padding: "0 12px",
    marginBottom: 8,
    borderRadius: 10,
    border: `1px solid ${REFINED.line}`,
    color: REFINED.ink,
    font: `400 16px ${REFINED.fontUI}`,
};

const hintStyle: React.CSSProperties = {
    color: REFINED.ink3,
    font: `400 12px ${REFINED.fontUI}`,
    lineHeight: 1.4,
};

const sheetGhostBtn: React.CSSProperties = {
    minHeight: 44,
    padding: "0 16px",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    background: REFINED.white,
    color: REFINED.ink2,
    font: `600 14px ${REFINED.fontUI}`,
    cursor: "pointer",
};

const sheetPrimaryBtn: React.CSSProperties = {
    minHeight: 44,
    padding: "0 20px",
    border: "none",
    borderRadius: 10,
    background: REFINED.aub,
    color: REFINED.white,
    font: `700 14px ${REFINED.fontUI}`,
    cursor: "pointer",
    marginLeft: "auto",
};

const calNameStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 6,
};

const ownTagStyle: React.CSSProperties = {
    padding: "1px 6px",
    borderRadius: 6,
    background: REFINED.aub100,
    color: REFINED.aub700,
    font: `700 10px ${REFINED.fontUI}`,
    textTransform: "uppercase",
    letterSpacing: 0.3,
};

export default MobileCalendarShell;
